/**
 * Product splash hero copy + CTA flags (Announcements popup campaign).
 * Plain text only: no HTML. Title / message remain fallbacks for headline / body.
 */

import { buildCartLineFromTierProduct } from './customerPricingContract';
import { isCatalogVisibleToCustomer, isEnquiryOnlyProduct } from './productCatalogVisibility';
import { resolveAnnouncementImageFitFromDoc } from './announcementImageFit';

export const PRODUCT_SPLASH_DEFAULT_EYEBROW = 'New this week';
export const SPLASH_ADD_FAIL_COPY =
    'This product could not be added. Open the product page to order.';
export const PRODUCT_SPLASH_DEFAULT_ADD_LABEL = 'Add to cart';
export const PRODUCT_SPLASH_DEFAULT_VIEW_LABEL = 'View product';
export const PRODUCT_SPLASH_CONTINUE_LABEL = 'Continue shopping';

export function splashPlainText(raw) {
    return String(raw ?? '').replace(/\r\n/g, '\n');
}

export function resolveProductSplashCopy(data = {}) {
    const headline = String(data.headline || data.title || '').trim();
    const body = String(data.body || data.message || '').trim();
    const eyebrow = String(data.eyebrow || '').trim();
    const footnote = String(data.footnote || '').trim();
    return { eyebrow, headline, body, footnote };
}

/** Blank line starts a new paragraph. Single newlines stay inside a paragraph. */
export function splitSplashParagraphs(raw) {
    const text = splashPlainText(raw).trim();
    if (!text) return [];
    return text
        .split(/\n{2,}/)
        .map((block) => block.replace(/^\n+|\n+$/g, ''))
        .filter(Boolean);
}

export function resolveProductSplashCtas(data = {}) {
    const showAddToCart = data.showAddToCart !== false;
    const showViewProduct = data.showViewProduct !== false;
    const addToCartLabel =
        String(data.addToCartText || '').trim() || PRODUCT_SPLASH_DEFAULT_ADD_LABEL;
    const viewProductLabel =
        String(data.ctaText || data.viewProductText || '').trim() || PRODUCT_SPLASH_DEFAULT_VIEW_LABEL;
    return {
        showAddToCart,
        showViewProduct,
        addToCartLabel,
        viewProductLabel,
    };
}

/** Product photo on the card. Off = use uploaded background instead (SKU stays attached). */
export function isAnnouncementProductImageEnabled(data = {}) {
    return data.showProductImage !== false;
}

/** Catalogue photo from Pick product, if stored separately from a custom hero URL. */
export function resolveAnnouncementProductImageUrl(data = {}) {
    return String(data.productImageUrl || data.imageUrl || '').trim();
}

/**
 * Image shown as the announcement/splash hero.
 * When showProductImage is false the product photo is hidden even if still stored.
 */
export function resolveAnnouncementDisplayImageUrl(data = {}) {
    if (!isAnnouncementProductImageEnabled(data)) return '';
    return resolveAnnouncementProductImageUrl(data);
}

/** Gold LSC header strip only when there is no product photo and no background image. */
export function shouldShowProductSplashBrandStrip(data = {}) {
    const hasHero = Boolean(resolveAnnouncementDisplayImageUrl(data));
    const hasBackground = Boolean(String(data.backgroundImageUrl || '').trim());
    if (hasHero || hasBackground) return false;
    return data.showLogo !== false;
}

export function getProductSplashMotionClass(prefersReducedMotion) {
    return prefersReducedMotion ? 'lsc-splash-motion-none' : 'lsc-splash-motion';
}

export function buildProductSplashHeroModel(data = {}) {
    const copy = resolveProductSplashCopy(data);
    const ctas = resolveProductSplashCtas(data);
    const imageUrl = resolveAnnouncementDisplayImageUrl(data);
    const backgroundImageUrl = String(data.backgroundImageUrl || '').trim();
    return {
        ...copy,
        ...ctas,
        imageUrl,
        backgroundImageUrl,
        hasHeroImage: Boolean(imageUrl),
        hasBackgroundImage: Boolean(backgroundImageUrl),
        imageFit: resolveAnnouncementImageFitFromDoc(data, 'image'),
        backgroundImageFit: resolveAnnouncementImageFitFromDoc(data, 'backgroundImage'),
        showBrandStrip: shouldShowProductSplashBrandStrip(data),
        paragraphs: splitSplashParagraphs(copy.body),
        continueLabel: PRODUCT_SPLASH_CONTINUE_LABEL,
        productSku: String(data.productSku || '').trim(),
    };
}

/**
 * Quantity defaults to 1. Enquiry-only and hidden SKUs cannot be added.
 * @returns {{ ok: true, line: object } | { ok: false, reason: string }}
 */
export function buildSplashAddToCartLine(row, pricingKey, quantity = 1) {
    if (!row || typeof row !== 'object') return { ok: false, reason: 'missing' };
    if (isEnquiryOnlyProduct(row)) return { ok: false, reason: 'enquiry' };
    if (!isCatalogVisibleToCustomer(row)) return { ok: false, reason: 'unavailable' };
    const qty = Math.max(1, Number(quantity) || 1);
    const line = buildCartLineFromTierProduct(row, pricingKey, { quantity: qty });
    if (!line?.code) return { ok: false, reason: 'missing' };
    return { ok: true, line };
}
